import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import Error from "next/error";
import { graphql } from "react-relay";

import { CollectionDetailsPageQuery } from "../../__generated__/CollectionDetailsPageQuery.graphql";
import { SlugAllCollectionsQuery } from "../../__generated__/SlugAllCollectionsQuery.graphql";
import CollectionDetailsPage from "../../components/pages/CollectionDetailsPage";
import { fetchStaticQuery } from "../../lib/relay/static-fetch";
import { extractNodes } from "../../lib/utils/connection";

type PageProps = {
  statusCode?: number;
  slug?: string;
};

const CollectionPage: NextPage<PageProps> = (props) => {
  if (props.statusCode || !props.slug) {
    return <Error statusCode={props.statusCode ?? 404} />;
  }

  return <CollectionDetailsPage slug={props.slug} />;
};

type StaticPathParams = {
  slug: string;
};

export const getStaticProps: GetStaticProps<
  PageProps,
  StaticPathParams
> = async (ctx) => {
  const slug = ctx.params?.slug;

  if (typeof slug !== "string") {
    return { props: { statusCode: 404 } };
  }

  const props = await fetchStaticQuery<CollectionDetailsPageQuery>(
    CollectionDetailsPage.query,
    { slug }
  );

  if (!props.collection) {
    return { props: { statusCode: 404 } };
  }

  return {
    props: {
      ...props,
      slug: slug,
    },
    revalidate: 10, // In seconds
  };
};

const allCollectionsQuery = graphql`
  query SlugAllCollectionsQuery {
    collections(first: 5000) {
      edges {
        node {
          slug
        }
      }
    }
  }
`;

export const getStaticPaths: GetStaticPaths<StaticPathParams> = async () => {
  const res = await fetchStaticQuery<SlugAllCollectionsQuery>(
    allCollectionsQuery
  );

  const collections = extractNodes(res.collections);

  return {
    paths: collections.map((collection) => ({
      params: { slug: collection.slug },
    })),
    fallback: "blocking",
  };
};

export default CollectionPage;
